import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);

  // Generate floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  // Track mouse for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 flex items-center justify-center overflow-hidden relative">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
        }} />
      </div>

      {/* Floating Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-gradient-to-r from-purple-400 to-pink-400 opacity-30"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            animation: `float ${particle.duration}s ease-in-out ${particle.delay}s infinite`,
          }}
        />
      ))}

      {/* Glowing Orbs */}
      <div 
        className="absolute w-96 h-96 rounded-full bg-purple-600/20 blur-3xl"
        style={{
          transform: `translate(${mousePos.x * 2}px, ${mousePos.y * 2}px)`,
          transition: 'transform 0.3s ease-out',
          top: '10%',
          left: '10%',
        }}
      />
      <div 
        className="absolute w-72 h-72 rounded-full bg-pink-600/20 blur-3xl"
        style={{
          transform: `translate(${-mousePos.x * 1.5}px, ${-mousePos.y * 1.5}px)`,
          transition: 'transform 0.3s ease-out',
          bottom: '20%',
          right: '15%',
        }}
      />
      <div 
        className="absolute w-64 h-64 rounded-full bg-cyan-600/15 blur-3xl"
        style={{
          transform: `translate(${mousePos.x}px, ${-mousePos.y}px)`,
          transition: 'transform 0.3s ease-out',
          top: '50%',
          right: '30%',
        }}
      />

      {/* Main Content */}
      <div 
        className="relative z-10 text-center px-6"
        style={{
          transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
          transition: 'transform 0.2s ease-out',
        }}
      >
        {/* 404 Number with Glitch Effect */}
        <div className="relative mb-8">
          <h1 
            className="text-[12rem] md:text-[16rem] font-black leading-none tracking-tighter bg-gradient-to-br from-white via-purple-200 to-pink-200 bg-clip-text text-transparent select-none"
            style={{
              textShadow: '0 0 80px rgba(139, 92, 246, 0.5)',
              animation: 'pulse404 3s ease-in-out infinite',
            }}
          >
            404
          </h1>
          
          {/* Glitch layers */}
          <h1 
            className="absolute top-0 left-0 text-[12rem] md:text-[16rem] font-black leading-none tracking-tighter text-cyan-400 opacity-50 select-none"
            style={{
              clipPath: 'inset(10% 0 85% 0)',
              animation: 'glitch1 2.5s ease-in-out infinite',
            }}
          >
            404
          </h1>
          <h1 
            className="absolute top-0 left-0 text-[12rem] md:text-[16rem] font-black leading-none tracking-tighter text-pink-400 opacity-50 select-none"
            style={{
              clipPath: 'inset(85% 0 5% 0)',
              animation: 'glitch2 2s ease-in-out infinite',
            }}
          >
            404
          </h1>
        </div>

        {/* Error Message */}
        <div className="mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Lost in the Digital Void
          </h2>
          <p className="text-lg md:text-xl text-purple-200/80 max-w-md mx-auto leading-relaxed">
            The page you're looking for doesn't exist or has drifted into another dimension.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-purple-300/60 font-mono mt-6">
            <span className="inline-block w-2 h-2 bg-red-400 rounded-full animate-pulse" />
            <span>Requested:</span>
            <code className="bg-white/5 px-3 py-1 rounded-lg border border-white/10">
              {location.pathname}
            </code>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate(-1)}
            className="group relative px-8 py-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 text-white font-semibold transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/20"
          >
            <span className="relative z-10 flex items-center gap-3">
              <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Go Back
            </span>
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="group relative px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/40 overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <span className="relative z-10 flex items-center gap-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Return Home
            </span>
          </button>
        </div>

        {/* Fun Decorative Elements */}
        <div className="mt-16 flex justify-center gap-6 opacity-40">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-1 h-12 bg-gradient-to-b from-purple-400 to-transparent rounded-full"
              style={{
                animation: `barBounce 1.5s ease-in-out ${i * 0.1}s infinite`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Scan Lines Effect */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
        }}
      />

      {/* Keyframe Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-30px) rotate(180deg); opacity: 0.6; }
        }
        @keyframes gridMove {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes pulse404 {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes glitch1 {
          0%, 100% { transform: translate(0); }
          20% { transform: translate(-3px, 3px); }
          40% { transform: translate(3px, -3px); }
          60% { transform: translate(-2px, 2px); }
          80% { transform: translate(2px, -2px); }
        }
        @keyframes glitch2 {
          0%, 100% { transform: translate(0); }
          25% { transform: translate(2px, -2px); }
          50% { transform: translate(-3px, 3px); }
          75% { transform: translate(3px, -2px); }
        }
        @keyframes barBounce {
          0%, 100% { transform: scaleY(0.3); opacity: 0.2; }
          50% { transform: scaleY(1); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default NotFound;
